import React, { useMemo } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import { Subject, SubjectVersion, Chapter, KnowledgePoint } from '../types';

interface SubjectVersionFilterProps {
  // 数据
  subjects: Subject[];
  subjectVersions: SubjectVersion[];
  chapters?: Chapter[];
  knowledgePoints?: KnowledgePoint[];

  // 选中值
  selectedSubjectId?: number | '';
  selectedVersionId?: number | '';
  selectedChapterId?: number | '';
  selectedKnowledgePointId?: number | '';

  // 回调
  onSubjectChange?: (subjectId: number | '') => void;
  onVersionChange?: (versionId: number | '') => void;
  onChapterChange?: (chapterId: number | '') => void;
  onKnowledgePointChange?: (knowledgePointId: number | '') => void;

  // 显示项
  showVersions?: boolean;
  showChapters?: boolean;
  showKnowledgePoints?: boolean;

  // 尺寸与样式
  size?: 'small' | 'medium';
  sx?: any;

  // 标签
  subjectLabel?: string;
  versionLabel?: string;
  chapterLabel?: string;
  knowledgePointLabel?: string;

  // 禁用
  disabled?: boolean;
}

const SubjectVersionFilter: React.FC<SubjectVersionFilterProps> = ({
  subjects = [],
  subjectVersions = [],
  chapters = [],
  knowledgePoints = [],

  selectedSubjectId = '',
  selectedVersionId = '',
  selectedChapterId = '',
  selectedKnowledgePointId = '',

  onSubjectChange,
  onVersionChange,
  onChapterChange,
  onKnowledgePointChange,

  showVersions = true,
  showChapters = false,
  showKnowledgePoints = false,

  size = 'small',
  sx = {},

  subjectLabel = '学科',
  versionLabel = '教材版本',
  chapterLabel = '章节',
  knowledgePointLabel = '知识点',

  disabled = false,
}) => {
  // 根据选中学科过滤版本
  const filteredVersions = useMemo(() => {
    if (!selectedSubjectId) return subjectVersions;
    const selectedSubject = subjects.find(s => s.id === selectedSubjectId);
    return selectedSubject?.versions || [];
  }, [selectedSubjectId, subjects, subjectVersions]);

  const filteredChapters = useMemo(() => {
    if (!selectedVersionId || !chapters) return [];
    return chapters.filter(ch => ch.subjectVersionId === selectedVersionId);
  }, [selectedVersionId, chapters]);

  const filteredKnowledgePoints = useMemo(() => {
    if (!selectedChapterId || !knowledgePoints) return [];
    return knowledgePoints.filter(kp => kp.chapterId === selectedChapterId);
  }, [selectedChapterId, knowledgePoints]);

  const handleSubjectChange = (subjectId: number | '') => {
    onSubjectChange?.(subjectId);
    onVersionChange?.('');
    onChapterChange?.('');
    onKnowledgePointChange?.('');
  };

  const handleVersionChange = (versionId: number | '') => {
    onVersionChange?.(versionId);
    onChapterChange?.('');
    onKnowledgePointChange?.('');
  };

  const handleChapterChange = (chapterId: number | '') => {
    onChapterChange?.(chapterId);
    onKnowledgePointChange?.('');
  };

  const getVersionDisplayName = (version: SubjectVersion) => {
    return version.schoolLevel ? `${version.name}(${version.schoolLevel})` : version.name;
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ...sx }}>
      {/* 学科选择 */}
      <FormControl size={size} sx={{ minWidth: 120 }}>
        <InputLabel>{subjectLabel}</InputLabel>
        <Select
          value={selectedSubjectId}
          label={subjectLabel}
          onChange={(e) => handleSubjectChange(e.target.value as number | '')}
          disabled={disabled}
        >
          <MenuItem value="">全部{subjectLabel}</MenuItem>
          {subjects.map((subject) => (
            <MenuItem key={subject.id} value={subject.id}>
              {subject.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* 教材版本选择 */}
      {showVersions && (
        <FormControl size={size} sx={{ minWidth: 200 }}>
          <InputLabel>{versionLabel}</InputLabel>
          <Select
            value={selectedVersionId}
            label={versionLabel}
            disabled={!selectedSubjectId || disabled}
            onChange={(e) => handleVersionChange(e.target.value as number | '')}
          >
            <MenuItem value="">
              {!selectedSubjectId ? `请先选择${subjectLabel}` : `全部${versionLabel}`}
            </MenuItem>
            {filteredVersions.map((version) => (
              <MenuItem key={version.id} value={version.id}>
                {getVersionDisplayName(version)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* 章节选择（可选） */}
      {showChapters && (
        <FormControl size={size} sx={{ minWidth: 150 }}>
          <InputLabel>{chapterLabel}</InputLabel>
          <Select
            value={selectedChapterId}
            label={chapterLabel}
            disabled={!selectedVersionId || disabled}
            onChange={(e) => handleChapterChange(e.target.value as number | '')}
          >
            <MenuItem value="">
              {!selectedVersionId ? `请先选择${versionLabel}` : `全部${chapterLabel}`}
            </MenuItem>
            {filteredChapters.map((chapter) => (
              <MenuItem key={chapter.id} value={chapter.id}>
                {chapter.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* 知识点选择（可选） */}
      {showKnowledgePoints && (
        <FormControl size={size} sx={{ minWidth: 150 }}>
          <InputLabel>{knowledgePointLabel}</InputLabel>
          <Select
            value={selectedKnowledgePointId}
            label={knowledgePointLabel}
            disabled={!selectedChapterId || disabled}
            onChange={(e) => onKnowledgePointChange?.(e.target.value as number | '')}
          >
            <MenuItem value="">
              {!selectedChapterId ? `请先选择${chapterLabel}` : `全部${knowledgePointLabel}`}
            </MenuItem>
            {filteredKnowledgePoints.map((kp) => (
              <MenuItem key={kp.id} value={kp.id}>
                {kp.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};

export default SubjectVersionFilter;

